import { useState } from 'react'
import { ChevronDown, ChevronRight, X, Plus, ListX } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface GlobalIgnoreListProps {
  symbols: string[]
  alertType?: string
  onUpdate?: (symbols: string[]) => void
  isUpdating?: boolean
  compact?: boolean
}

export function GlobalIgnoreList({ 
  symbols, 
  alertType = 'alerts',
  onUpdate,
  isUpdating = false,
  compact = false,
}: GlobalIgnoreListProps) {
  const [isOpen, setIsOpen] = useState(!compact)
  const [newSymbol, setNewSymbol] = useState('')

  const parseSymbols = (input: string): string[] => {
    const tokens = input
      .split(/[,\n]/)
      .map((item) => item.trim().toUpperCase())
      .filter((item) => item.length > 0)

    const seen = new Set<string>()
    const unique: string[] = []
    for (const symbol of tokens) {
      if (seen.has(symbol)) continue
      seen.add(symbol)
      unique.push(symbol)
    }
    return unique
  }

  const handleAddSymbol = () => {
    const incoming = parseSymbols(newSymbol)
    if (incoming.length > 0 && onUpdate) {
      const existing = new Set(symbols)
      const toAdd = incoming.filter((symbol) => !existing.has(symbol))
      if (toAdd.length === 0) {
        setNewSymbol('')
        return
      }
      onUpdate([...symbols, ...toAdd])
      setNewSymbol('')
    }
  }

  const handleRemoveSymbol = (symbolToRemove: string) => {
    if (onUpdate) {
      onUpdate(symbols.filter(s => s !== symbolToRemove))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSymbol()
    }
  }

  const canEdit = !!onUpdate

  // Compact mode: just a button with popover
  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <ListX className="mr-2 h-4 w-4" />
            Ignore List
            <Badge variant="secondary" className="ml-2">
              {symbols.length}
            </Badge>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96" align="end">
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-sm mb-1">Global Ignore List</h4>
              <p className="text-xs text-muted-foreground">
                Symbols ignored across all {alertType}
              </p>
            </div>
            
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  placeholder="Add symbols (e.g., BTC, ETH)"
                  value={newSymbol}
                  onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  disabled={isUpdating}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={handleAddSymbol}
                  disabled={!newSymbol.trim() || isUpdating}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto">
              {symbols.map((symbol) => (
                <Badge key={symbol} variant="outline" className="px-2 py-1">
                  <span>{symbol}</span>
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemoveSymbol(symbol)
                      }}
                      disabled={isUpdating}
                      className="ml-1.5 hover:bg-muted rounded-full p-0.5 transition-colors disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
              {symbols.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  No symbols in ignore list
                </span>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Full mode: collapsible card
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Global Ignore List
                  <Badge variant="secondary" className="ml-2">
                    {symbols.length}
                  </Badge>
                </CardTitle>
                <CardDescription className="ml-6">
                  Symbols ignored across all {alertType}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
                {isOpen ? 'Hide' : 'Show'}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  placeholder="Add symbols (e.g., BTC, ETH)"
                  value={newSymbol}
                  onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  disabled={isUpdating}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={handleAddSymbol}
                  disabled={!newSymbol.trim() || isUpdating}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              {symbols.map((symbol) => (
                <Badge key={symbol} variant="outline" className="px-2 py-1">
                  <span>{symbol}</span>
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemoveSymbol(symbol)
                      }}
                      disabled={isUpdating}
                      className="ml-1.5 hover:bg-muted rounded-full p-0.5 transition-colors disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
              {symbols.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  No symbols in ignore list
                </span>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
